// Barre du bas (mobile, app), avec son menu dropup (hooks/useDropup).
//
// Bouton IA → chatbot : window.dispatchEvent(new CustomEvent('northvane:chatbot:toggle')).
// Le composant Chatbot écoute cet événement sur window et bascule ouvert/fermé.
//
// Données : ReactShell::BottomNav (app/controllers/concerns/react_shell/bottom_nav.rb).
// Cibles du tour guidé (data-onboarding) : explore-menu pour le Congrès,
// chatbot pour le bouton IA.
// Le défilement en haut après chaque visite est fait par app/global.ts.
import { useEffect, useState } from 'react'
import type { MouseEvent } from 'react'
import { router, usePage } from '@inertiajs/react'
import AppLink from '@/components/ui/AppLink'
import { useDropup } from '@/hooks/useDropup'
import { t } from '@/lib/i18n'
import { isActiveFromUrl } from '@/components/shell/bottom_nav/activeFromUrl'

export const CHATBOT_TOGGLE_EVENT = 'northvane:chatbot:toggle'

type SignedOutData = {
  signedIn: false
  hrefs: { login: string; signup: string; blog: string }
}

type SignedInData = {
  signedIn: true
  labels: { stocks: string; explore: string; analysis: string; analyzer: string; simulations: string }
  hrefs: {
    dashboard: string
    opportunities: string
    congress: string
    marketRadar: string
    stockAnalysis: string
    graphAnalyses: string
    simulations: string
    predictionMarkets: string
    account: string
    accountPortfolio: string
    accountInvestorProfile: string
    accountOpportunities: string
    companySettings: string
    blog: string
  }
  active: {
    stocks: boolean
    dashboard: boolean
    opportunities: boolean
    congress: boolean
    marketRadar: boolean
    predictionMarkets: boolean
    explore: boolean
    stockAnalysis: boolean
    graphAnalyses: boolean
    simulations: boolean
    profile: boolean
    account: boolean
    accountPortfolio: boolean
    accountInvestorProfile: boolean
    accountOpportunities: boolean
    companySettings: boolean
  }
  badges: { stocks: number; dashboard: boolean; graphAnalyses: number; simulations: number }
  unvisited: { opportunities: boolean; stockAnalysis: boolean; graphAnalyses: boolean; simulations: boolean; predictionMarkets: boolean; chatbot: boolean }
  stocksGlow: boolean
  show: { graphAnalyses: boolean; simulations: boolean; investorProfile: boolean; company: boolean }
  ai: { locked: boolean; remaining: number | null } | null
}

export type BottomNavData = SignedOutData | SignedInData

type DirectItem = 'blog' | 'login' | 'signup'

const glow = (on: boolean) => (on ? ' undiscovered-glow' : '')

// Retours à la ligne entre éléments : sans effet sur le rendu (conteneurs
// flex), mais la barre est masquée sur desktop et le texte d'un élément
// masqué est son textContent, où ils séparent les mots.
const NL = '\n'

export default function BottomNav({ data: rawData }: { data: Record<string, any> }) {
  const data = rawData as BottomNavData
  const { url } = usePage()
  const dropup = useDropup<'stocks' | 'explore' | 'profile'>()

  // Entrée touchée, marquée active sans attendre la réponse ; l'état serveur
  // et l'URL reprennent la main à la fin de la visite.
  const [tapped, setTapped] = useState<DirectItem | null>(null)
  useEffect(
    () =>
      router.on('finish', (event) => {
        if (event.detail.visit.prefetch || event.detail.visit.async) return
        setTapped(null)
      }),
    [],
  )

  const tap = (item: DirectItem) => () => {
    if (document.body.classList.contains('webview-mode')) setTapped(item)
  }
  const directClass = (item: DirectItem, href: string) => {
    const active = tapped ? tapped === item : isActiveFromUrl(url, href)
    return `webview-nav-item${active ? ' active' : ''}`
  }
  const dropupActive = (active: boolean) => (tapped ? false : active)

  const toggleChatbot = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    window.dispatchEvent(new CustomEvent(CHATBOT_TOGGLE_EVENT))
  }

  if (!data.signedIn) {
    const { hrefs } = data
    return (
      <nav className="webview-bottom-nav">
        {NL}
        <div className="webview-nav-container">
          {NL}
          {/* Not signed in: show login/signup links */}
          {NL}
          <AppLink href={hrefs.login} className={directClass('login', hrefs.login)} onClick={tap('login')}>
            {NL}
            <i className="fas fa-sign-in-alt"></i>
            {NL}
            <span className="webview-nav-label">{t('nav.login')}</span>
            {NL}
          </AppLink>
          {NL}
          <AppLink href={hrefs.signup} className={directClass('signup', hrefs.signup)} onClick={tap('signup')}>
            {NL}
            <i className="fas fa-user-plus"></i>
            {NL}
            <span className="webview-nav-label">{t('nav.signup')}</span>
            {NL}
          </AppLink>
          {NL}
          {/* Blog */}
          {NL}
          <AppLink href={hrefs.blog} className={directClass('blog', hrefs.blog)} onClick={tap('blog')}>
            {NL}
            <i className="fas fa-newspaper"></i>
            {NL}
            <span className="webview-nav-label">Blog</span>
            {NL}
          </AppLink>
        </div>
        {NL}
      </nav>
    )
  }

  const { hrefs, active, badges, unvisited, show, labels, ai } = data
  const itemClass = (on: boolean) => `webview-dropup-item${on ? ' active' : ''}`

  return (
    <nav className="webview-bottom-nav">
      {NL}
      <div className="webview-nav-container">
        {NL}
        {NL}
        {/* 1. Stock Market (Dashboard + Stock Analysis + Graph Analyzer + Simulations) */}
        {NL}
        <div
          ref={dropup.container('stocks')}
          className={`webview-nav-item webview-nav-dropup${dropupActive(active.stocks) ? ' active' : ''}`}
        >
          {NL}
          <button type="button" className="webview-nav-dropup-trigger" onClick={(e) => dropup.toggle('stocks', e)}>
            {NL}
            <i className={`fas fa-chart-line${glow(data.stocksGlow)}`}></i>
            {NL}
            <span className="webview-nav-label">{labels.stocks}</span>
            {NL}
            {badges.stocks > 0 && (
              <>
                <span className="webview-nav-badge">{badges.stocks}</span>
                {NL}
              </>
            )}
          </button>
          {NL}
          <div className={`webview-dropup-menu${dropup.open === 'stocks' ? ' open' : ''}`}>
            {NL}
            <AppLink href={hrefs.dashboard} className={itemClass(active.dashboard)} onClick={dropup.close}>
              {NL}
              <i className="fas fa-chart-pie"></i>
              {NL}
              <span>{t('nav.dashboard')}</span>
              {NL}
              {badges.dashboard && (
                <>
                  <span className="webview-dropup-badge">1</span>
                  {NL}
                </>
              )}
            </AppLink>
            <AppLink href={hrefs.stockAnalysis} className={itemClass(active.stockAnalysis)} onClick={dropup.close}>
              {NL}
              <i className={`fas fa-microscope${glow(unvisited.stockAnalysis)}`}></i>
              {NL}
              <span>{labels.analysis}</span>
              {NL}
            </AppLink>
            {show.graphAnalyses && (
              <AppLink href={hrefs.graphAnalyses} className={itemClass(active.graphAnalyses)} onClick={dropup.close}>
                {NL}
                <i className={`fas fa-chart-area${glow(unvisited.graphAnalyses)}`}></i>
                {NL}
                <span>{labels.analyzer}</span>
                {NL}
                {badges.graphAnalyses > 0 && (
                  <>
                    <span className="webview-dropup-badge">{badges.graphAnalyses}</span>
                    {NL}
                  </>
                )}
              </AppLink>
            )}
            {show.simulations && (
              <AppLink href={hrefs.simulations} className={itemClass(active.simulations)} onClick={dropup.close}>
                {NL}
                <i className={`fas fa-rocket${glow(unvisited.simulations)}`}></i>
                {NL}
                <span>{labels.simulations}</span>
                {NL}
                {badges.simulations > 0 && (
                  <>
                    <span className="webview-dropup-badge">{badges.simulations}</span>
                    {NL}
                  </>
                )}
              </AppLink>
            )}
          </div>
          {NL}
        </div>
        {NL}
        {NL}
        {/* 2. Explore (Opportunities, Market Radar, US Congress, Polymarket): where
            ideas come from, the stock picks first and the prediction markets last. */}
        {NL}
        <div
          ref={dropup.container('explore')}
          className={`webview-nav-item webview-nav-dropup${dropupActive(active.explore) ? ' active' : ''}`}
        >
          {NL}
          <button type="button" className="webview-nav-dropup-trigger" data-onboarding="explore-menu" onClick={(e) => dropup.toggle('explore', e)}>
            {NL}
            <i className={`fas fa-compass${glow(unvisited.opportunities || unvisited.predictionMarkets)}`}></i>
            {NL}
            <span className="webview-nav-label">{labels.explore}</span>
            {NL}
          </button>
          {NL}
          <div className={`webview-dropup-menu${dropup.open === 'explore' ? ' open' : ''}`}>
            {NL}
            <AppLink href={hrefs.opportunities} className={itemClass(active.opportunities)} onClick={dropup.close}>
              {NL}
              <i className={`fas fa-lightbulb${glow(unvisited.opportunities)}`}></i>
              {NL}
              <span>{t('sidebar.opportunities', { default: 'Opportunities' })}</span>
              {NL}
            </AppLink>
            <AppLink href={hrefs.marketRadar} className={itemClass(active.marketRadar)} onClick={dropup.close}>
              {NL}
              <i className="fas fa-satellite-dish"></i>
              {NL}
              <span>{t('market_radar.title', { default: 'Market Radar' })}</span>
              {NL}
            </AppLink>
            <AppLink href={hrefs.congress} className={itemClass(active.congress)} onClick={dropup.close}>
              {NL}
              <i className="fas fa-landmark"></i>
              {NL}
              <span>{t('congress.title', { default: 'US Congress' })}</span>
              {NL}
            </AppLink>
            <AppLink href={hrefs.predictionMarkets} className={itemClass(active.predictionMarkets)} onClick={dropup.close}>
              {NL}
              <i className={`fas fa-balance-scale${glow(unvisited.predictionMarkets)}`}></i>
              {NL}
              <span>Polymarket</span>
              {NL}
            </AppLink>
          </div>
          {NL}
        </div>
        {NL}
        {/* 3. AI Agent (Chatbot) - CENTER */}
        {NL}
        {ai && (
          <>
            <button type="button" className="webview-nav-item webview-nav-item-ai" id="webview-ai-agent-btn" data-onboarding="chatbot" onClick={toggleChatbot}>
              {NL}
              <i className={`fas fa-robot${glow(unvisited.chatbot)}`}></i>
              {NL}
              <span className="webview-nav-label">AI</span>
              {NL}
              {ai.locked ? (
                <>
                  <span className="webview-nav-badge webview-nav-badge-locked">
                    {NL}
                    <i className="fas fa-lock"></i>
                    {NL}
                  </span>
                  {NL}
                </>
              ) : (
                ai.remaining !== null && (
                  <>
                    <span className="webview-nav-badge webview-nav-badge-requests">{ai.remaining}</span>
                    {NL}
                  </>
                )
              )}
            </button>
            {NL}
          </>
        )}
        {NL}
        {/* 4. Profile (Account + Portfolio + Investor Profile + Opportunities + Company) */}
        {NL}
        <div
          ref={dropup.container('profile')}
          className={`webview-nav-item webview-nav-dropup webview-nav-dropup-right${dropupActive(active.profile) ? ' active' : ''}`}
        >
          {NL}
          <button type="button" className="webview-nav-dropup-trigger" onClick={(e) => dropup.toggle('profile', e)}>
            {NL}
            <i className="fas fa-user-cog"></i>
            {NL}
            <span className="webview-nav-label">{t('nav.preferences')}</span>
            {NL}
          </button>
          {NL}
          <div className={`webview-dropup-menu${dropup.open === 'profile' ? ' open' : ''}`}>
            {NL}
            <AppLink href={hrefs.account} className={itemClass(active.account)} onClick={dropup.close}>
              {NL}
              <i className="fas fa-user"></i>
              {NL}
              <span>{t('account.tabs.account_short', { default: 'Account' })}</span>
              {NL}
            </AppLink>
            <AppLink href={hrefs.accountPortfolio} className={itemClass(active.accountPortfolio)} onClick={dropup.close}>
              {NL}
              <i className="fas fa-briefcase"></i>
              {NL}
              <span>{t('sidebar.portfolio', { default: 'Portfolio' })}</span>
              {NL}
            </AppLink>
            {show.investorProfile && (
              <AppLink href={hrefs.accountInvestorProfile} className={itemClass(active.accountInvestorProfile)} onClick={dropup.close}>
                {NL}
                <i className="fas fa-sliders-h"></i>
                {NL}
                <span>{t('sidebar.investor_profile', { default: 'Investor Profile' })}</span>
                {NL}
              </AppLink>
            )}
            <AppLink href={hrefs.accountOpportunities} className={itemClass(active.accountOpportunities)} onClick={dropup.close}>
              {NL}
              <i className="fas fa-lightbulb"></i>
              {NL}
              <span>{t('sidebar.opportunities', { default: 'Opportunities' })}</span>
              {NL}
            </AppLink>
            {show.company && (
              <AppLink href={hrefs.companySettings} className={itemClass(active.companySettings)} onClick={dropup.close}>
                {NL}
                <i className="fas fa-building"></i>
                {NL}
                <span>{t('sidebar.company', { default: 'Company' })}</span>
                {NL}
              </AppLink>
            )}
          </div>
          {NL}
        </div>
        {NL}
        {NL}
        {/* 5. Blog */}
        {NL}
        <AppLink href={hrefs.blog} className={directClass('blog', hrefs.blog)} onClick={tap('blog')}>
          {NL}
          <i className="fas fa-newspaper"></i>
          {NL}
          <span className="webview-nav-label">Blog</span>
          {NL}
        </AppLink>
      </div>
      {NL}
    </nav>
  )
}
